import type { ItemFieldRow } from './item-field-reader';
import { readInventoryTemplateId } from './item-field-reader';
import { phpJsonTruthy } from './php-json-values';

function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function instanceId(row: ItemFieldRow): string | null {
    const raw = own(own(row, 'inventory'), 'inventory_id') ?? own(row, 'inventory_id');
    const id = arrayLike(raw) ? own(raw, '0') : raw;
    return validUuid(id) ? id : null;
}
export function isThinnedInventoryRead(row: ItemFieldRow): boolean {
    if (readInventoryTemplateId(row) === null || instanceId(row) === null) return false;
    const layers = own(row, 'item_layer_types');
    if (layers == null) return true;
    if (!arrayLike(layers)) return false;
    if (Object.keys(layers).every(key => key === 'current_item_charges')) return true;
    if (phpJsonTruthy(own(own(row, 'inventory'), 'enchantment_template_id'))) return true;
    const flat = own(row, 'enchantment_template_id');
    if (phpJsonTruthy(arrayLike(flat) ? own(flat, '0') : flat)) return true;
    return own(own(row, 'computed'), 'enchantment') != null;
}
export interface InventoryReadContext {
    read: <T>(row: ItemFieldRow, read: (full: ItemFieldRow) => T) => T | null;
    /** Suppress the same instance bridge during a local read; preserve an outer guard. */
    withoutInstanceBridge: <T>(row: ItemFieldRow, read: () => T) => T;
}
/** One request-owned _ft_inv_bridge_read guard, shared by scalar/list/vitality readers
 * and supplied to every stage factory participating in that request.
 * Resolution occurs before marking the ID active, matching PHP; the supplied resolver
 * owns its own memo/re-entry contract. A known null differs from an unavailable input.
 */
export function createInventoryReadContext(resolve: (id: string) => ItemFieldRow | null): InventoryReadContext {
    const active = new Set<string>();
    return { read: (row, read) => {
        const id = instanceId(row);
        if (id === null || active.has(id)) return null;
        const full = resolve(id);
        if (full === null) return null;
        active.add(id);
        try { return read(full); }
        finally { active.delete(id); }
    }, withoutInstanceBridge: (row, read) => {
        const id = instanceId(row);
        if (id === null || active.has(id)) return read();
        active.add(id);
        try { return read(); }
        finally { active.delete(id); }
    } };
}
